import { KatFileUpload } from '@amzn/katal-react';
import React, { FC, useEffect, useMemo } from 'react';
import { StyledRadioButtonGroup } from 'src/components/general/StyledRadioButtonGroup';
import { ConfirmationDialog } from 'src/components/general/ConfirmationDialog';
import { StatusIndicator } from 'src/components/general/StatusIndicator';
import { useAssetInput } from 'src/hooks/assetInput/useAssetInput';
import { InputType, AssetStatus } from 'src/types';
import { UrlInput } from './UrlInput';
import { AssetInputProps, INPUT_OPTIONS } from '.';
import styles from './AssetInput.module.css';
import { getFileUploadState } from 'src/utils/assetInput/assetInputUtils';
import { StateConstants } from 'src/constants';
import { StyledDropdown } from 'src/components/general/StyledDropdown';
import { CMSConfig } from 'src/configs/campaignMetadataConfig';
import { PropUtils } from 'src/utils/PropUtils';
import { useAssetContext } from 'src/hooks/useAssetContext';
import { useSelection } from 'src/contexts/SelectionContext';
import { useAuthentication } from 'src/contexts/AuthenticationContext';
import { getUserAlias } from 'src/components/authentication/AuthenticationUtils';
import { useCampaignMetadata } from 'src/hooks/useCampaignMetadata';
import { abort } from 'process';

/**
 * Component for handling asset input through various methods (URL, file upload, or logo text)
 * @param {AssetInputProps} props - The component props
 * @returns {JSX.Element} Asset input component with radio buttons for input type selection
 */
export const AssetInput: FC<AssetInputProps> = ({
  label,
  assetInputOptions,
  assetInputHandler,
  acceptedFileTypes,
  maxFileSizeInBytes,
  assetTransformationHandler,
  error,
  status,
  assetType,
  assetDetails,
  assetValidationHandler,
}) => {
  const {
    selectedType,
    url,
    showConfirmDialog,
    disableClearUrlButton,
    radioGroupName,
    isValidUrlInput,
    handleUrlChange,
    handleUrlFetch,
    handleUrlRemoval,
    handleFileAttached,
    handleFileRemoved,
    handleFileReplaced,
    handleFileAttachedError,
    handleRadioChange,
    handleConfirmTypeChange,
    handleCancelTypeChange,
    handleTransformationTemplateChange,
    getConfirmationMessage,
  } = useAssetInput({
    label,
    assetInputHandler,
    status,
    assetDetails,
    assetValidationHandler,
  });

  const { assetStates } = useAssetContext();
  const { placement } = useSelection();
  const { metadata } = useCampaignMetadata();
  const { user } = useAuthentication();
  const fileUploadStatus = getFileUploadState(status, error);

  const radioButtonInputOptions = Object.keys(assetInputOptions).map(
    (option) => INPUT_OPTIONS[option as InputType],
  );

  const transformationTemplate = assetStates[assetType].transformationTemplate;
  const userAlias = useMemo(() => getUserAlias(user), [user]);

  useEffect(() => {
    if (status === AssetStatus.PENDING_TRANSFORMATION) {
      assetTransformationHandler.handleTransformation({
        assetType,
        transformationTemplate,
        placement,
        actor: userAlias,
        provider: metadata.provider,
        sourceAssetKey: assetDetails.key,
      });
    }
  }, [status, userAlias, transformationTemplate, placement, metadata.provider, assetDetails.key]);

  const renderInput = () => {
    switch (selectedType) {
      case InputType.URL:
        return (
          <UrlInput
            url={url}
            status={status}
            onChange={handleUrlChange}
            onFetch={handleUrlFetch}
            onClear={handleUrlRemoval}
            isValidUrl={isValidUrlInput}
            isFetching={status === AssetStatus.PENDING_UPLOAD}
            disableClearUrlButton={disableClearUrlButton}
          />
        );
      case InputType.UPLOAD:
        return (
          <KatFileUpload
            accept={acceptedFileTypes}
            maxSize={maxFileSizeInBytes}
            onFilesAttached={(event) => handleFileAttached(event)}
            onFilesRemoved={handleFileRemoved}
            onFilesReplaced={(event) => handleFileReplaced(event)}
            onFilesAttachedError={handleFileAttachedError}
            hideClearAll
            state={fileUploadStatus}
            data-testid="file-upload"
          >
            <div slot="error">{error}</div>
          </KatFileUpload>
        );
      // TODO: Implement Logo Text Input type
      // JIRA: https://issues.labcollab.net/browse/EDSDAP-9873

      default:
        return null;
    }
  };

  return (
    <div className={styles.container} data-testid="asset-input">
      <div className={styles.radioGroup}>
        <StyledRadioButtonGroup
          name={radioGroupName}
          options={radioButtonInputOptions}
          value={selectedType}
          onChange={handleRadioChange}
          label={label}
          data-testid="input-type-selector"
        />
      </div>

      {renderInput()}

      <StatusIndicator
        error={selectedType === InputType.URL ? error : StateConstants.EMPTY_STRING} // for Input type of file upload input error is thrown in KatFileUpload component
        isLoading={[
          AssetStatus.PENDING_UPLOAD,
          AssetStatus.PENDING_TRANSFORMATION,
          AssetStatus.TRANSFORMATION_IN_PROGRESS,
        ].includes(status)}
        isSuccess={status === AssetStatus.COMPLETED}
        data-testid="asset-input-status"
      />

      {assetInputOptions[selectedType as InputType] && (
        <StyledDropdown
          options={PropUtils.createOptionsFromEnumValues(
            CMSConfig.TRANSFORMATION_TEMPLATE[assetType],
          )}
          value={assetStates[assetType].transformationTemplate}
          onChange={handleTransformationTemplateChange}
          label={'Transformation Template'}
          data-testid={`${assetType}-transformation-template-selector`}
          disabled={status === AssetStatus.BASIC_VALIDATION_FAILED}
        />
      )}

      <ConfirmationDialog
        isVisible={showConfirmDialog}
        title="Change Input"
        message={getConfirmationMessage()}
        onConfirm={handleConfirmTypeChange}
        onCancel={handleCancelTypeChange}
        data-testid="asset-input-confirm-dialog"
      />
    </div>
  );
};
